import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AiOutlineDoubleLeft } from 'react-icons/ai';
import axios from 'config/axios';
import Table from 'components/Table';

import './index.css';

type VocabSetsProps = {
    vocabStudents: any[];
    vocabQuizStatus: any;
    updateVocabSets: boolean;
};

function VocabSets({ vocabStudents, vocabQuizStatus, updateVocabSets }: VocabSetsProps) {
    const [allSets, setAllSets] = useState([]);
    const [assignedSets, setAssignedSets] = useState([]);
    const [totalStudents, setTotalStudents] = useState(0);
    const [assigning, setAssigning] = useState<number | null>(null);

    const { classId } = useParams();

    const fetchSets = () => {
        axios.get('vocab/tutor/sets/')
            .then(res => setAllSets(res.data.vocabSets))
            .catch(error => console.error(error));

        axios.get(`vocab/tutor/classes/${classId}/vocab-sets/`)
            .then(res => {
                setAssignedSets(res.data.assignedSets || []);
                setTotalStudents(res.data.totalStudents || 0);
            })
            .catch(() => setAssignedSets([]));
    };

    useEffect(() => {
        fetchSets();
    }, [classId]);

    const handleAssign = (vocabSetId: number) => {
        setAssigning(vocabSetId);
        axios.post('vocab/tutor/assign-class/', {
            classId: parseInt(classId),
            vocabSetId: vocabSetId,
        })
            .then(() => {
                setAssigning(null);
                fetchSets();
            })
            .catch(error => {
                setAssigning(null);
                console.error(error);
            });
    };

    // Build a map of assigned set data
    const assignedMap = {};
    for (const s of assignedSets) {
        assignedMap[s.id] = s;
    }

    if (updateVocabSets) {
        // Fully assigned = every student has the set
        const fullyAssigned = assignedSets.filter(s => s.assignedCount >= totalStudents);
        const fullyAssignedIds = new Set(fullyAssigned.map(s => s.id));

        // Available = all sets not fully assigned
        const available = allSets.filter(vs => !fullyAssignedIds.has(vs.id));

        return (
            <div className='class-vocab-sets'>
                <Table
                    className='class-vocab-sets__table'
                    header={['Assigned to All', '# Words']}
                    rows={fullyAssigned.map(vs => ([
                        vs.subject,
                        vs.wordCount,
                    ]))}
                />
                <AiOutlineDoubleLeft className='class-vocab-sets__arrow' />
                <Table
                    className='class-vocab-sets__table'
                    header={['', 'Vocab Set', '# Words', 'Assigned']}
                    rows={available.map(vs => {
                        const assigned = assignedMap[vs.id];
                        const count = assigned ? assigned.assignedCount : 0;
                        return [
                            assigning === vs.id ? 'Assigning...' : 'Assign All',
                            vs.subject,
                            vs.wordCount,
                            `${count}/${totalStudents}`,
                        ];
                    })}
                    onSelectCell={available.map(vs => ([
                        assigning ? null : () => handleAssign(vs.id),
                        null,
                        null,
                        null,
                    ]))}
                />
            </div>
        );
    }

    return (
        <div className='class-vocab-sets class-vocab-sets--full'>
            <Table
                header={['Subject', '# Words', 'Students Assigned']}
                rows={assignedSets.map(vs => ([
                    vs.subject,
                    vs.wordCount,
                    `${vs.assignedCount}/${totalStudents}`,
                ]))}
                navigator
                defaultRowsPerPage={10}
            />
            {assignedSets.length === 0 && (
                <p className='class-vocab-sets__empty'>No vocab sets assigned to this class yet.</p>
            )}
        </div>
    );
}

export default VocabSets;
